import React, { useCallback, useEffect, useState } from "react";
import "../../Revenue/revenue.css";
import { getStoreWalletTransactions, updateStoreCommission } from "./marketMoneyApi";
import { errorMessage } from "../../Revenue/revenueApi";
import { Loading, Pager } from "../../Revenue/parts";
import { dateTime } from "../../Revenue/format";

const TYPE_LABELS = {
  commission_debit: "Commission charged",
  payment_credit: "Payment received",
  adjustment: "Adjustment",
};

/// Store detail → the store's commission rate (own or default) and every
/// movement of its commission wallet. `refreshKey` reloads after a credit.
export default function StoreMoneyPanel({ storeId, refreshKey }) {
  const [page, setPage] = useState(1);
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");
  const [rate, setRate] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const r = await getStoreWalletTransactions(storeId, { page, limit: 10 });
      setRes(r);
      setRate(r.store.custom_rate ? String(r.store.commission_rate) : "");
    } catch (e) {
      setError(errorMessage(e, "Could not load the wallet"));
    }
  }, [storeId, page]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const save = async (value) => {
    setSaving(true);
    setMsg(null);
    try {
      const r = await updateStoreCommission(storeId, value === "" ? null : parseFloat(value));
      setMsg({ ok: true, text: `Now ${r.commission_rate}% (${r.custom_rate ? "own rate" : "default"}) — for orders delivered from now on.` });
      await load();
    } catch (e) {
      setMsg({ ok: false, text: errorMessage(e, "Could not save") });
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className="rv-error mb-4">{error}</div>;
  if (!res) return <Loading />;

  const s = res.store;
  const cur = s.currency || "";

  return (
    <div className="rv-wrap mb-4">
      <div className="row g-3">
        <div className="col-lg-4">
          <div className="rv-panel h-100">
            <div className="rv-panel-title">Commission</div>
            <div className="rv-kpi-value mb-1">{s.commission_rate}%</div>
            <p className="rv-sub mb-3">{s.custom_rate ? "This store's own rate" : "Using the default rate"}</p>
            <label className="rv-label">Own rate (%) — empty = default</label>
            <input
              className="rv-input mb-2"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
            />
            <div className="d-flex gap-2">
              <button className="rv-btn violet flex-grow-1" disabled={saving} onClick={() => save(rate)}>
                {saving ? "Saving…" : "Save"}
              </button>
              {s.custom_rate && (
                <button className="rv-btn ghost" disabled={saving} onClick={() => save("")}>
                  Use default
                </button>
              )}
            </div>
            {msg && <div className={`mt-3 ${msg.ok ? "rv-success" : "rv-error"}`}>{msg.text}</div>}
          </div>
        </div>
        <div className="col-lg-8">
          <div className="rv-panel h-100">
            <div className="rv-panel-title">Commission wallet history</div>
            {!res.data.length ? (
              <div className="rv-empty">No movements yet.</div>
            ) : (
              <>
                <div className="rv-table-wrap">
                  <table className="rv-table" style={{ minWidth: 560 }}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>What</th>
                        <th className="rv-num">Amount</th>
                        <th className="rv-num">Balance after</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {res.data.map((t) => (
                        <tr key={t.id}>
                          <td className="small text-grey" style={{ whiteSpace: "nowrap" }}>
                            {dateTime(t.created_at)}
                          </td>
                          <td>
                            {TYPE_LABELS[t.type] || t.type}
                            {t.order_id ? <div className="small text-grey">Order #{t.order_id}</div> : null}
                          </td>
                          <td className={`rv-num ${t.amount >= 0 ? "rv-amount-pos" : "rv-amount-neg"}`}>
                            {t.amount >= 0 ? "+" : ""}
                            {Number(t.amount).toLocaleString("en-US", { maximumFractionDigits: 2 })} {cur}
                          </td>
                          <td className="rv-num">
                            {Number(t.balance_after).toLocaleString("en-US", { maximumFractionDigits: 2 })} {cur}
                          </td>
                          <td className="small text-grey">{t.note || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pager meta={res.meta} onPage={setPage} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
